import { useEffect, useState } from "react";
import { Menu, X, Phone, Snowflake } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { href: "#services", label: "Services" },
  { href: "#about", label: "About" },
  { href: "#booking", label: "Book" },
  { href: "#reviews", label: "Reviews" },
  { href: "#areas", label: "Service Area" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-100 shadow-sm"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2.5">
          <div className="w-12 h-12 rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden flex items-center justify-center">
            <img
              src="/brand/koehn-logo.jpg"
              alt="Koehn Cooling logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-lg tracking-tight text-slate-900">
              Koehn Cooling
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Hiawatha · Kansas
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-slate-600 hover:text-[#E31837] font-medium transition-colors text-sm"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:7852880330"
            data-testid="header-phone-link"
            className="inline-flex items-center gap-2 text-slate-700 hover:text-[#0F172A] font-semibold text-sm"
          >
            <Phone className="w-4 h-4" /> 785-288-0330
          </a>
          <Button
            data-testid="header-book-btn"
            onClick={() =>
              document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })
            }
            className="rounded-full bg-[#E31837] hover:bg-[#C8102E] text-white px-6 h-11 font-semibold shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
          >
            Book Appointment
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-xl hover:bg-slate-100"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-100 bg-white">
          <div className="px-4 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-2 text-slate-700 font-medium"
              >
                {n.label}
              </a>
            ))}
            <Button
              data-testid="mobile-book-btn"
              onClick={() => {
                setOpen(false);
                document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="rounded-full bg-[#E31837] hover:bg-[#C8102E] text-white mt-2 h-11"
            >
              Book Appointment
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
